import React from "react";
import { Calculator, RotateCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { fmtEUR, fmtUSD, fmtPct, PARTNER_LABELS } from "@/pages/cotizador/shared";

const PctInput = ({ value, onChange, testid }) => (
  <input type="number" step="0.5" min="0" value={value ?? 0} onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
    className="h-6 w-16 rounded border bg-background px-1 text-right text-sm tabular-nums outline-none focus:ring-1 focus:ring-ring" data-testid={testid} />
);

const Line = ({ label, value, testid, strong }) => (
  <div className={`flex items-center justify-between ${strong ? "text-sm font-semibold" : "text-sm"}`}>
    <div className={strong ? "" : "text-muted-foreground"}>{label}</div>
    <div className="tabular-nums" data-testid={testid}>{value}</div>
  </div>
);

export function CotCostSummary({ result, version, doc, partners, onSet }) {
  const t = result?.totals;
  const rule = (partners || []).find((p) => p.key === (doc?.partner || "other"));

  const applyAuto = () => {
    if (!rule) { toast.error("Partner desconocido"); return; }
    onSet({ markup_pct: rule.markup_pct, commission_pct: rule.commission_pct, commission_mode: rule.commission_mode });
    toast.success(`Markup ${rule.markup_pct}% · Comisión ${rule.commission_pct}% aplicados`);
  };

  return (
    <Card data-testid="cot-cost-summary">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Calculator className="h-4 w-4 text-primary" /> Coste
          {version ? <Badge variant="outline" className="ml-auto text-[10px]">motor v{version}</Badge> : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {!t ? (
          <p className="text-sm text-muted-foreground">Añade servicios o alojamientos para calcular el precio.</p>
        ) : (
          <>
            <Line label="Subtotal con IVA" value={fmtEUR(t.cost_tax_incl)} testid="cot-sub-incl" />
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span>Markup</span>
                <PctInput value={doc.markup_pct} onChange={(v) => onSet({ markup_pct: v })} testid="cot-markup-input" />
                <span className="text-xs">%</span>
              </div>
              <div className="tabular-nums" data-testid="cot-markup-eur">+ {fmtEUR(t.markup_eur)}</div>
            </div>
            {t.uplift_eur > 0 && <Line label="Uplift partner" value={`+ ${fmtEUR(t.uplift_eur)}`} />}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span>Donation</span>
                <input type="number" step="1" min="0" value={doc.pax_fee_eur ?? 10} onChange={(e) => onSet({ pax_fee_eur: parseFloat(e.target.value || "0") })}
                  className="h-6 w-16 rounded border bg-background px-1 text-right text-sm tabular-nums outline-none focus:ring-1 focus:ring-ring" data-testid="cot-pax-fee-input" />
                <span className="text-xs">€ × {doc.num_travelers || 1} pax</span>
              </div>
              <div className="tabular-nums" data-testid="cot-pax-fee-eur">+ {fmtEUR(t.pax_fee_eur || 0)}</div>
            </div>
            <Line label="Subtotal cotizado" value={fmtEUR(t.subtotal_quoted)} testid="cot-subtotal" strong />
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span>Comisión <span className="text-xs">({PARTNER_LABELS[doc.partner] || doc.partner})</span></span>
                <PctInput value={doc.commission_pct} onChange={(v) => onSet({ commission_pct: v })} testid="cot-commission-input" />
                <span className="text-xs">%</span>
                <button onClick={applyAuto} className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground hover:bg-muted hover:text-primary" data-testid="cot-auto-partner" title={`Auto: markup ${rule?.markup_pct}% · comisión ${rule?.commission_pct}%`}>
                  <RotateCw className="h-3 w-3" /> Auto
                </button>
              </div>
              <div className="tabular-nums" data-testid="cot-commission-eur">+ {fmtEUR(t.commission_eur)}</div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex cursor-pointer items-center gap-2 text-muted-foreground">
                <Switch checked={!!doc.paypal_fee} onCheckedChange={(v) => onSet({ paypal_fee: v })} data-testid="cot-paypal" />
                <span>Recargo PayPal <span className="text-xs">(+3%)</span></span>
              </label>
              <div className="tabular-nums" data-testid="cot-paypal-eur">{doc.paypal_fee ? `+ ${fmtEUR(t.paypal_eur)}` : "—"}</div>
            </div>

            <div className="mt-1 flex items-center justify-between rounded-md bg-foreground px-3 py-2.5 text-background" data-testid="cot-pvp-final">
              <span className="text-[11px] uppercase tracking-wide opacity-70">PVP final</span>
              <span className="text-xl font-bold tabular-nums" data-testid="cot-pvp-eur">{fmtEUR(t.pvp_eur)}</span>
            </div>
            {doc.num_travelers > 1 && (
              <div className="flex items-center justify-between text-sm" data-testid="cot-pvp-per-pax">
                <span className="text-muted-foreground">PVP por persona <span className="text-xs">({doc.num_travelers} pax)</span></span>
                <span className="font-semibold tabular-nums">{fmtEUR(t.pvp_eur / doc.num_travelers)}</span>
              </div>
            )}

            <div className="rounded-md border bg-muted/30 p-2.5">
              <div className="mb-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="uppercase tracking-wide">Cambio EUR → USD</span>
                <span className="ml-auto">a gusto del agente</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">1 € =</span>
                <input type="number" step="0.01" min="0" value={doc.fx_rate ?? ""} placeholder="1.08"
                  onChange={(e) => onSet({ fx_rate: e.target.value })}
                  className="h-7 w-24 rounded border bg-background px-1.5 text-right text-sm tabular-nums outline-none focus:ring-1 focus:ring-ring" data-testid="cot-fx-input" />
                <span className="text-xs text-muted-foreground">USD</span>
              </div>
              <div className="mt-2 flex items-center justify-between rounded bg-primary px-3 py-2 text-primary-foreground">
                <span className="text-[11px] uppercase tracking-wide opacity-80">PVP en USD</span>
                <span className="text-lg font-bold tabular-nums" data-testid="cot-pvp-usd">{t.pvp_usd > 0 ? fmtUSD(t.pvp_usd) : "—"}</span>
              </div>
            </div>

            <div className="border-t pt-2"><Line label="Margen" value={`${fmtEUR(t.margin_eur)} · ${fmtPct(t.margin_pct)}`} testid="cot-margin" /></div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
